// QuotationFilePreview.js — durable target for PDF eye-icon links.
// Presigned S3 URLs expire (~15 min); this page re-fetches a fresh preview_url with auth
// and shows the image in its own window (no Innoworx app shell).
import { Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatApiError, resolveQuotationFilePreviewUrl } from './interiorQuotationApi';

export const buildQuotationFilePreviewHref = (siteId, fileId) => {
  if (!siteId || !fileId || typeof window === 'undefined') return '';
  const params = new URLSearchParams({
    siteId: String(siteId),
    fileId: String(fileId),
  });
  return `${window.location.origin}/onboarding-interior-quotation/file-preview?${params.toString()}`;
};

const QuotationFilePreview = () => {
  const [searchParams] = useSearchParams();
  const siteId = searchParams.get('siteId') || '';
  const fileId = searchParams.get('fileId') || '';
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!siteId || !fileId) {
        setError('Missing site or file id.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const url = await resolveQuotationFilePreviewUrl(siteId, fileId, {
          forceRefresh: true,
        });
        if (cancelled) return;
        if (!url) {
          setError('Image not found or you do not have access.');
          return;
        }
        setPreviewUrl(url);
      } catch (err) {
        if (!cancelled) setError(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [siteId, fileId]);

  return (
    <div
      className="flex min-h-screen w-screen flex-col items-center justify-center bg-neutral-950 p-4"
      style={{ colorScheme: 'dark' }}
    >
      {loading && (
        <Loader2 className="h-6 w-6 animate-spin text-teal-400" />
      )}
      {!loading && error && (
        <div className="max-w-md rounded-xl border border-red-400/40 bg-red-950/60 px-4 py-3 text-center text-sm text-red-200">
          {error}
        </div>
      )}
      {!loading && previewUrl && (
        <img
          src={previewUrl}
          alt="Quotation line item"
          className="max-h-[100vh] max-w-[100vw] object-contain"
        />
      )}
    </div>
  );
};

export default QuotationFilePreview;
